import { useCallback, useEffect, useRef, useState } from 'react'
import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPostJson } from '../../../api/client'
import { Modal } from '../../../ui/Modal'
import type { Point, Positions } from './positions'
import './savedViews.css'

/**
 * ADM -> Map: VIEW-URILE SALVATE.
 *
 * Harta se aseaza singura la fiecare incarcare, deci ce muta omul de mana se
 * pierdea la refresh, si nimeni altcineva nu-l vedea. Un view salvat tine
 * STAREA hartii - filtrele din bara, asezarea, gruparea, zoomul si pozitiile
 * nodurilor - sub un nume:
 *
 *  - al ORGANIZATIEI: definit o data, vazut la fel de toata lumea (zece oameni
 *    din management deschid aceeasi poza). Il scrie numai cine are dreptul de
 *    editare al hartii; unul poate fi implicit, si se deschide singur;
 *  - PERSONAL: al omului, nevazut de altcineva.
 *
 * STAREA E GENERICA. Bara hartii creste (asezare, grupare, filtre), iar cheile
 * noi intra in view fara sa le stie fisierul asta: tot ce e in `query`, plus
 * `viewport` si `positions`. Cheile pe care ecranul de azi nu le are se
 * PASTREAZA la salvare, nu se pierd.
 *
 * ULTIMA STARE, FARA SALVARE. Separat de view-uri, ce muta omul se tine in
 * browserul lui (localStorage), pe harta (root + vedere) sau pe view-ul
 * deschis - ca o mutare sa nu se piarda la refresh. Nu pleaca nicaieri.
 */

export interface Viewport {
  zoom: number
  pan: Point
}

/** Starea hartii, cum se salveaza. Orice alta cheie trece neatinsa. */
export type MapViewState = Record<string, unknown> & {
  viewport?: Viewport | null
  positions?: Positions
}

export interface SavedViewItem {
  id: number
  name: string
  visibility: 'organization' | 'private'
  isDefault: boolean
  mine: boolean
  canEdit: boolean
  updatedAt: string
}

export interface SavedViewFull extends SavedViewItem {
  state: MapViewState
}

export interface SavedViewList {
  ready: boolean
  canEditOrganization: boolean
  defaultId: number
  saved: SavedViewItem[]
}

const EMPTY: SavedViewList = { ready: false, canEditOrganization: false, defaultId: 0, saved: [] }

/* ------------------------------------------------------------------ *
 * API - /api/v2/adm/map/views
 * ------------------------------------------------------------------ */

/**
 * Raspunsul citit cu grija: o lista fara `saved` (server vechi, sau alt capat)
 * e o lista goala, nu o cadere a hartii.
 */
function asList(body: unknown): SavedViewList {
  if (typeof body !== 'object' || body === null || !Array.isArray((body as SavedViewList).saved)) return EMPTY
  const b = body as SavedViewList
  return {
    ready: b.ready === true,
    canEditOrganization: b.canEditOrganization === true,
    defaultId: Number(b.defaultId) || 0,
    saved: b.saved,
  }
}

export const savedViewsQuery = queryOptions({
  queryKey: ['adm', 'map', 'saved-views'],
  queryFn: async () => asList(await apiGet<unknown>('/adm/map/views')),
  staleTime: 30000,
})

export function fetchSavedView(id: number): Promise<SavedViewFull> {
  return apiGet<SavedViewFull>(`/adm/map/views/${id}`)
}

export function createSavedView(name: string, visibility: string, state: MapViewState): Promise<SavedViewFull> {
  return apiPostJson<SavedViewFull>('/adm/map/views', { name, visibility, state })
}

export function updateSavedView(id: number, patch: { name?: string; visibility?: string; state?: MapViewState }): Promise<SavedViewFull> {
  return apiPostJson<SavedViewFull>(`/adm/map/views/${id}`, patch)
}

export function deleteSavedView(id: number): Promise<unknown> {
  return apiPostJson(`/adm/map/views/${id}/delete`, {})
}

export function setDefaultSavedView(id: number, on: boolean): Promise<unknown> {
  return apiPostJson(`/adm/map/views/${id}/default`, { on })
}

/* ------------------------------------------------------------------ *
 * Starea
 * ------------------------------------------------------------------ */

/** Ce din stare merge in `query` (bara de filtre si cheile celorlalti). */
export function queryFromState(state: MapViewState): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(state)) {
    if (key === 'viewport' || key === 'positions') continue
    out[key] = value
  }
  return out
}

function stable(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stable).join(',')}]`
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>).filter(([, v]) => v !== undefined)
    entries.sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stable(v)}`).join(',')}}`
  }
  return JSON.stringify(value ?? null)
}

/** Aceleasi filtre? Ordinea cheilor nu conteaza, `undefined` = lipsa. */
export function sameQuery(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
  return stable(a) === stable(b)
}

/* ------------------------------------------------------------------ *
 * Ultima stare, in browser
 * ------------------------------------------------------------------ */

export interface LastState {
  /** `updatedAt` al view-ului peste care s-a mutat; gol pe harta fara view. */
  base: string
  positions: Positions
  viewport: Viewport | null
}

const LAST_PREFIX = 'od.admMap.last.v1'

export function lastKey(userId: number, scope: string): string {
  return `${LAST_PREFIX}:${userId}:${scope}`
}

/** Pe harta fara view: root si vedere. */
export function mapScope(root: unknown, view: unknown): string {
  return `map:${Number(root) || 0}:${String(view ?? '')}`
}

export function viewScope(id: number): string {
  return `view:${id}`
}

export function readLast(key: string): LastState | null {
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw) as LastState
    if (!parsed || typeof parsed !== 'object' || typeof parsed.positions !== 'object' || parsed.positions === null) return null
    return { base: String(parsed.base ?? ''), positions: parsed.positions, viewport: parsed.viewport ?? null }
  } catch {
    return null
  }
}

/** Pozitiile noi PESTE cele vechi: un nod ascuns acum de un filtru isi pastreaza locul. */
export function writeLast(key: string, patch: Partial<LastState>): void {
  try {
    const old = readLast(key)
    const next: LastState = {
      base: patch.base ?? old?.base ?? '',
      positions: { ...(old?.positions ?? {}), ...(patch.positions ?? {}) },
      viewport: patch.viewport !== undefined ? patch.viewport : (old?.viewport ?? null),
    }
    window.localStorage.setItem(key, JSON.stringify(next))
  } catch {
    /* Stocare plina sau blocata: harta merge mai departe, doar fara memorie. */
  }
}

export function forgetLast(key: string): void {
  try {
    window.localStorage.removeItem(key)
  } catch {
    /* nimic de facut */
  }
}

/* ------------------------------------------------------------------ *
 * Adresa
 * ------------------------------------------------------------------ */

function setUrlView(id: number): void {
  try {
    const url = new URL(window.location.href)
    if (id > 0) url.searchParams.set('view', String(id))
    else url.searchParams.delete('view')
    window.history.replaceState(window.history.state, '', url.toString())
  } catch {
    /* fara adresa schimbata - view-ul ramane deschis oricum */
  }
}

export function viewLink(id: number): string {
  return `${window.location.origin}${window.location.pathname}?view=${id}`
}

async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch {
    /* cade pe execCommand */
  }
  try {
    const area = document.createElement('textarea')
    area.value = text
    document.body.appendChild(area)
    area.select()
    const ok = document.execCommand('copy')
    area.remove()
    return ok
  } catch {
    return false
  }
}

/* ------------------------------------------------------------------ *
 * Hook-ul
 * ------------------------------------------------------------------ */

export interface SavedViewsOptions {
  /** `?view=<id>` din adresa; 0 = nimic cerut. */
  requestedId: number
  /** Adresa n-a cerut nimic anume (nici view, nici root): se deschide implicitul. */
  autoOpenDefault: boolean
  userId: number
  /** S-a aflat cine e omul? Ultima lui stare e pe numele lui, deci se asteapta. */
  userReady: boolean
  /** Filtrele de acum - pentru indicatorul „modified". */
  query: Record<string, unknown>
  /** Starea de acum a hartii, intreaga. */
  capture: () => MapViewState
  /** Pune o stare pe harta; intoarce `query` rezultat. */
  apply: (state: MapViewState) => Record<string, unknown>
}

export interface SavedViews {
  list: SavedViewList
  /** Serverul raspunde, dar tabelul nu exista inca (inainte de migrare). */
  notReady: boolean
  /** S-a hotarat ce se deschide (view cerut, implicit, sau nimic)? Harta asteapta asta. */
  settled: boolean
  active: SavedViewItem | null
  modified: boolean
  message: string
  busy: boolean
  open: (id: number) => Promise<void>
  close: () => void
  saveAs: (name: string, visibility: 'organization' | 'private') => Promise<boolean>
  save: () => Promise<void>
  rename: (name: string) => Promise<boolean>
  remove: () => Promise<void>
  toggleDefault: () => Promise<void>
  copyLink: () => Promise<void>
  /** Un nod mutat de mana. */
  markMoved: () => void
}

export function useSavedViews(options: SavedViewsOptions): SavedViews {
  const client = useQueryClient()
  const listQuery = useQuery(savedViewsQuery)
  const list = listQuery.data ?? EMPTY

  const [active, setActive] = useState<SavedViewItem | null>(null)
  const [baseline, setBaseline] = useState<Record<string, unknown>>({})
  const [moved, setMoved] = useState(false)
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)
  const [settled, setSettled] = useState(false)

  /* Starea incarcata, intreaga: cheile pe care ecranul de azi nu le tine se
     scriu inapoi la salvare. */
  const loaded = useRef<MapViewState>({})
  const started = useRef(false)

  const opts = useRef(options)
  opts.current = options

  const refresh = useCallback(() => client.invalidateQueries({ queryKey: savedViewsQuery.queryKey }), [client])

  const open = useCallback(async (id: number) => {
    setMessage('')
    setBusy(true)
    try {
      const view = await fetchSavedView(id)
      let state: MapViewState = view.state && typeof view.state === 'object' ? view.state : {}
      let movedSince = false

      /* Ce a mutat omul peste ACEST view, nesalvat, de la ultima lui
         schimbare: pus inapoi, si aratat ca modificat. */
      const last = readLast(lastKey(opts.current.userId, viewScope(id)))
      if (last && last.base === view.updatedAt && Object.keys(last.positions).length > 0) {
        state = { ...state, positions: { ...(state.positions ?? {}), ...last.positions }, viewport: last.viewport ?? state.viewport ?? null }
        movedSince = true
      }

      loaded.current = view.state ?? {}
      const query = opts.current.apply(state)
      const { state: _ignored, ...item } = view
      void _ignored
      setActive(item)
      setBaseline(query)
      setMoved(movedSince)
      setUrlView(id)
    } catch (error) {
      const status = (error as { status?: number }).status
      setMessage(status === 404 ? 'That saved view does not exist, or it is not shared with you.' : (error as Error).message)
    } finally {
      setBusy(false)
      setSettled(true)
    }
  }, [])

  /* Ce se deschide la intrare: view-ul din adresa, altfel implicitul
     organizatiei, altfel nimic. O singura data. */
  useEffect(() => {
    if (started.current || !options.userReady) return
    if (opts.current.requestedId > 0) {
      started.current = true
      void open(opts.current.requestedId)
      return
    }
    if (listQuery.isPending) return
    started.current = true
    if (opts.current.autoOpenDefault && list.defaultId > 0) {
      void open(list.defaultId)
      return
    }
    setSettled(true)
  }, [listQuery.isPending, list.defaultId, open, options.userReady])

  const close = useCallback(() => {
    setActive(null)
    setMoved(false)
    loaded.current = {}
    setUrlView(0)
  }, [])

  const snapshot = (): MapViewState => ({ ...loaded.current, ...opts.current.capture() })

  const settle = (view: SavedViewFull, query: Record<string, unknown>) => {
    const { state, ...item } = view
    loaded.current = state ?? {}
    setActive(item)
    setBaseline(query)
    setMoved(false)
    /* Salvat: ce era tinut in browser peste el nu mai are rost. */
    forgetLast(lastKey(opts.current.userId, viewScope(item.id)))
  }

  const run = async (work: () => Promise<void>): Promise<boolean> => {
    setMessage('')
    setBusy(true)
    try {
      await work()
      return true
    } catch (error) {
      setMessage((error as Error).message)
      return false
    } finally {
      setBusy(false)
    }
  }

  const saveAs = (name: string, visibility: 'organization' | 'private') =>
    run(async () => {
      /* Un view nou porneste din ce e pe ecran, nu din cel deschis: cheile lui
         necunoscute raman, dar numele si dreptul sunt ale celui nou. */
      const state = snapshot()
      const view = await createSavedView(name, visibility, state)
      settle(view, opts.current.query)
      setUrlView(view.id)
      await refresh()
    })

  const save = async () => {
    if (!active) return
    await run(async () => {
      const view = await updateSavedView(active.id, { state: snapshot() })
      settle(view, opts.current.query)
      await refresh()
    })
  }

  const rename = (name: string) =>
    run(async () => {
      if (!active) return
      const view = await updateSavedView(active.id, { name })
      const { state: _ignored, ...item } = view
      void _ignored
      setActive(item)
      await refresh()
    })

  const remove = async () => {
    if (!active) return
    const id = active.id
    const ok = await run(async () => {
      await deleteSavedView(id)
      await refresh()
    })
    if (ok) {
      forgetLast(lastKey(opts.current.userId, viewScope(id)))
      close()
    }
  }

  const toggleDefault = async () => {
    if (!active) return
    const on = !active.isDefault
    await run(async () => {
      await setDefaultSavedView(active.id, on)
      setActive({ ...active, isDefault: on })
      await refresh()
    })
  }

  const copyLink = async () => {
    if (!active) return
    const ok = await copyText(viewLink(active.id))
    setMessage(ok ? 'Link copied.' : `Copy this link: ${viewLink(active.id)}`)
  }

  const modified = active !== null && (moved || !sameQuery(baseline, options.query))

  return {
    list,
    notReady: listQuery.isSuccess && listQuery.data !== EMPTY && !list.ready,
    settled,
    active,
    modified,
    message,
    busy,
    open,
    close,
    saveAs,
    save,
    rename,
    remove,
    toggleDefault,
    copyLink,
    markMoved: useCallback(() => setMoved(true), []),
  }
}

/* ------------------------------------------------------------------ *
 * Bara
 * ------------------------------------------------------------------ */

type Dialog = '' | 'save' | 'rename' | 'delete'

export function SavedViewsBar({ views }: { views: SavedViews }) {
  const { list, active } = views
  const [dialog, setDialog] = useState<Dialog>('')
  const [name, setName] = useState('')
  const [visibility, setVisibility] = useState<'organization' | 'private'>('private')
  const [error, setError] = useState('')

  const organization = list.saved.filter((v) => v.visibility === 'organization')
  const mine = list.saved.filter((v) => v.visibility === 'private')
  const canSaveOver = active !== null && active.canEdit
  const closeDialog = useCallback(() => setDialog(''), [])

  const startSave = () => {
    setName(active ? `${active.name} (copy)` : '')
    setVisibility('private')
    setError('')
    setDialog('save')
  }

  const startRename = () => {
    setName(active?.name ?? '')
    setError('')
    setDialog('rename')
  }

  const submit = async () => {
    const trimmed = name.trim()
    if (trimmed === '') {
      setError('Give the view a name.')
      return
    }
    const ok = dialog === 'rename' ? await views.rename(trimmed) : await views.saveAs(trimmed, visibility)
    if (ok) setDialog('')
  }

  return (
    <div className="adm-views-bar" id="mapViewsBar">
      <div className="adm-field adm-views-field">
        <label htmlFor="mapSavedView">Views</label>
        <select
          className="form-control input-sm"
          id="mapSavedView"
          value={String(active?.id ?? 0)}
          disabled={views.busy}
          onChange={(e) => {
            const id = Number(e.target.value)
            if (id > 0) void views.open(id)
            else views.close()
          }}
        >
          <option value="0">{list.saved.length === 0 ? 'No saved views yet' : '— Not a saved view —'}</option>
          {organization.length > 0 ? (
            <optgroup label="Organization">
              {organization.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.isDefault ? `★ ${v.name}` : v.name}
                </option>
              ))}
            </optgroup>
          ) : null}
          {mine.length > 0 ? (
            <optgroup label="My views">
              {mine.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </optgroup>
          ) : null}
        </select>
      </div>

      {views.modified ? (
        <span className="adm-views-modified" id="mapViewModified" title="The map differs from the saved view">
          modified
        </span>
      ) : null}

      {active ? (
        <span className="adm-views-scope" title={active.visibility === 'organization' ? 'Everyone in the organization sees this view' : 'Only you see this view'}>
          <i className={active.visibility === 'organization' ? 'fas fa-users' : 'fas fa-user'}></i>
          {active.visibility === 'organization' ? ' Organization' : ' Only me'}
          {active.isDefault ? ' · default' : ''}
        </span>
      ) : null}

      <span className="adm-views-actions">
        {canSaveOver ? (
          <button
            className="btn btn-primary btn-sm"
            id="mapViewSave"
            disabled={views.busy || !views.modified}
            title="Save the map over this view"
            onClick={() => void views.save()}
          >
            <i className="fas fa-save"></i> Save
          </button>
        ) : null}
        <button className="btn btn-default btn-sm" id="mapViewSaveAs" disabled={views.busy || !list.ready} onClick={startSave}>
          <i className="fas fa-plus"></i> Save view
        </button>
        {canSaveOver ? (
          <>
            <button className="btn btn-default btn-sm" id="mapViewRename" disabled={views.busy} onClick={startRename}>
              Rename
            </button>
            <button className="btn btn-default btn-sm" id="mapViewDelete" disabled={views.busy} onClick={() => setDialog('delete')}>
              Delete
            </button>
          </>
        ) : null}
        {active && active.visibility === 'organization' && list.canEditOrganization ? (
          <button
            className="btn btn-default btn-sm"
            id="mapViewDefault"
            disabled={views.busy}
            title={active.isDefault ? 'Stop opening this view by default' : 'Open this view for everyone who opens the map'}
            onClick={() => void views.toggleDefault()}
          >
            <i className={active.isDefault ? 'fas fa-star' : 'far fa-star'}></i> {active.isDefault ? 'Default' : 'Set as default'}
          </button>
        ) : null}
        {active ? (
          <button className="btn btn-default btn-sm" id="mapViewLink" title="Copy a link that opens this view" onClick={() => void views.copyLink()}>
            <i className="fas fa-link"></i> Copy link
          </button>
        ) : null}
      </span>

      {views.notReady ? (
        <span className="adm-views-message" id="mapViewNotReady">
          Saved views are not available yet on this server.
        </span>
      ) : null}
      {views.message !== '' ? (
        <span className="adm-views-message" id="mapViewMessage">
          {views.message}
        </span>
      ) : null}

      <Modal
        id="mapViewDialog"
        open={dialog === 'save' || dialog === 'rename'}
        onClose={closeDialog}
        title={dialog === 'rename' ? 'Rename view' : 'Save view'}
        labelledBy="mapViewDialogTitle"
        portal
        footer={
          <>
            <button className="btn btn-default" onClick={closeDialog}>
              Cancel
            </button>
            <button className="btn btn-primary" id="mapViewDialogOk" disabled={views.busy} onClick={() => void submit()}>
              {dialog === 'rename' ? 'Rename' : 'Save'}
            </button>
          </>
        }
      >
        <div className="form-group">
          <label htmlFor="mapViewName">Name</label>
          <input
            className="form-control"
            id="mapViewName"
            maxLength={120}
            value={name}
            autoFocus
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void submit()
            }}
          />
        </div>
        {dialog === 'save' ? (
          <div className="form-group adm-views-visibility">
            <label>Who sees it</label>
            <div className="radio">
              <label>
                <input type="radio" name="mapViewVisibility" id="mapViewPrivate" checked={visibility === 'private'} onChange={() => setVisibility('private')} />
                Only me <span className="text-muted">— my own view, nobody else sees it</span>
              </label>
            </div>
            <div className={`radio${list.canEditOrganization ? '' : ' disabled'}`}>
              <label>
                <input
                  type="radio"
                  name="mapViewVisibility"
                  id="mapViewOrganization"
                  checked={visibility === 'organization'}
                  disabled={!list.canEditOrganization}
                  onChange={() => setVisibility('organization')}
                />
                Organization <span className="text-muted">— everyone who can open the map sees the same view</span>
              </label>
            </div>
            {!list.canEditOrganization ? (
              <p className="help-block">Saving a view for the whole organization needs the right to edit the ADM map.</p>
            ) : null}
          </div>
        ) : null}
        {error !== '' || (views.message !== '' && dialog !== '') ? (
          <div className="alert alert-warning">{error !== '' ? error : views.message}</div>
        ) : null}
      </Modal>

      <Modal
        id="mapViewDeleteDialog"
        open={dialog === 'delete'}
        onClose={closeDialog}
        title="Delete view"
        labelledBy="mapViewDeleteTitle"
        portal
        footer={
          <>
            <button className="btn btn-default" onClick={closeDialog}>
              Cancel
            </button>
            <button
              className="btn btn-danger"
              id="mapViewDeleteOk"
              disabled={views.busy}
              onClick={() => {
                setDialog('')
                void views.remove()
              }}
            >
              Delete
            </button>
          </>
        }
      >
        <p>
          Delete <strong>{active?.name}</strong>?
          {active?.visibility === 'organization' ? ' Everyone in the organization loses it.' : ''}
        </p>
      </Modal>
    </div>
  )
}
